// src/components/SummaryTabs.js
//
// 3-tab summary view (Processes, Products, Communication).
// Drives the audit lifecycle: draft → submitted → published.
//
//   draft     — user is still filling FormA-F. Submit button shown.
//   submitted — user has reviewed summaries. Publish button shown to AppAdmin only.
//               User can still go back and edit forms; summary recomputes live.
//   published — frozen. Summary copy written to Firestore. Forms locked elsewhere.
//
// Permissions:
//   - Submit (draft → submitted)  : AppAdmin + AppUser
//   - Publish (submitted → published) : AppAdmin ONLY
//     AppUsers see a disabled Publish button with a tooltip explaining why.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Tabs, Tab, Typography, Paper, Button, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from './firebase.config';
import { useAuth } from '../auth/AuthContext';
import { useFormContext } from '../context/FormContext';
import useAuditProgress from '../hooks/useAuditProgress';
import AuditSidebar from './AuditSidebar';
import { buildProcessData, buildPrecursorData } from '../utils/exportSummaryExcel';
import { exportSummaryPdf } from '../utils/exportSummaryPdf';
import { useProductManual } from '../context/ProductManualContext';
import Summary1Installation from './Summary1Installation';
import SummaryProducts from './SummaryProducts';
import SummaryCommunication from './SummaryCommunication';


const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`summary-tabpanel-${index}`}
      aria-labelledby={`summary-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index) => ({
  id: `summary-tab-${index}`,
  'aria-controls': `summary-tabpanel-${index}`,
});

const STATUS_COLORS = {
  draft:     'default',
  submitted: 'warning',
  published: 'success',
};

const SummaryTabs = ({ readOnly = false }) => {
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const navigate = useNavigate();
  //  NEW — pull isAppAdmin to gate the Publish button
  
  const { currentUser, isAppAdmin } = useAuth();
  const { allFormData } = useFormContext();
  const { data: manualData } = useProductManual();
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState(null); // 'draft' | 'submitted' | 'published'
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [error, setError] = useState('');
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);

  // Fetch the audit's current status on mount and whenever auditId changes.
  useEffect(() => {
    if (!auditId) {
      setLoadingStatus(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, 'auditReports', auditId));
        if (cancelled) return;
        if (snap.exists()) {
          // Treat legacy 'in_progress' status as 'draft' for backwards compatibility.
          const raw = snap.data().status || 'draft';
          setStatus(raw === 'in_progress' ? 'draft' : raw);
        } else {
          setError('Audit not found.');
        }
      } catch (err) {
        console.error('Error loading audit status:', err);
        if (!cancelled) setError('Failed to load audit status.');
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auditId]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // PDF download handler. Reads from current context.
  // Available in all audit states (draft / submitted / published).
  const handleDownloadPdf = () => {
    try {
      exportSummaryPdf(allFormData, manualData, { status });
    } catch (err) {
      console.error('PDF export failed:', err);
      setError('Failed to generate PDF. Check console for details.');
    }
  };

  const handleSubmit = async () => {
    setConfirmSubmitOpen(false);
    if (!auditId || !currentUser?.email) return;
    setActionInProgress(true);
    setError('');
    try {
      await updateDoc(doc(firestore, 'auditReports', auditId), {
        status: 'submitted',
        submittedAt: serverTimestamp(),
        submittedBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setStatus('submitted');
    } catch (err) {
      console.error('Submit failed:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setActionInProgress(false);
    }
  };

  // Plan B snapshot: freeze the computed summary data at publish time,
  // so future formula changes never alter a published audit.
  const buildSummarySnapshot = () => {
    const fA = allFormData?.formA || {};
    const fC = allFormData?.formC || {};

    const processData  = buildProcessData(allFormData);
    const precursorData = buildPrecursorData(allFormData);

    // Strip the _proc and _fF helper fields from processData — they're
    // transient references used during compute and can contain non-serializable
    // values. We only persist plain JSON.
    const cleanProcessData = processData.map(({ _proc, _fF, ...rest }) => rest);

    const totalDirect   = Number(fC?.directEmissions   || fC?.totalDirectEmissions   || 0);
    const totalIndirect = Number(fC?.indirectEmissions || fC?.totalIndirectEmissions || 0);
    const ghgTotals = {
      dirEm:       cleanProcessData.reduce((s, p) => s + (p.dirEm || 0), 0),
      heatEm:      cleanProcessData.reduce((s, p) => s + (p.heatEm || 0), 0),
      wgEm:        cleanProcessData.reduce((s, p) => s + (p.wgEm || 0), 0),
      totalDirect: cleanProcessData.reduce((s, p) => s + (p.totalDirect || 0), 0),
      indirect:    cleanProcessData.reduce((s, p) => s + (p.indirect || 0), 0),
    };

    return {
      // Schema versioning — bump if shape changes, so old snapshots can be migrated.
      summarySchemaVersion: 1,

      // Common installation context (same input feeds all 3 summaries).
      installation: {
        name:          fA.installationNameEnglish || fA.installationName || '',
        street:        fA.street          || '',
        economic:      fA.economicActivity || '',
        city:          fA.city            || '',
        country:       fA.country         || '',
        unlocode:      fA.unLocode        || '',
        latitude:      fA.latitude        || '',
        longitude:     fA.longitude       || '',
        periodStart: fA.reportingPeriodStart ? (typeof fA.reportingPeriodStart === 'string' ? fA.reportingPeriodStart : fA.reportingPeriodStart.toDate ? fA.reportingPeriodStart.toDate().toISOString() : String(fA.reportingPeriodStart)) : '',
        periodEnd: fA.reportingPeriodEnd ? (typeof fA.reportingPeriodEnd === 'string' ? fA.reportingPeriodEnd : fA.reportingPeriodEnd.toDate ? fA.reportingPeriodEnd.toDate().toISOString() : String(fA.reportingPeriodEnd)) : '',
        carbonPrice:   fA.carbonPriceInstrument || fA.carbonPrice || '',
        addlInfo:      fA.additionalInfo  || '',
      },

      // Tab 1 — Summary Processes
      summaryProcesses: {
        aggregatedGoods:    fA.aggregatedGoods    || [],
        relevantProcesses:  fA.relevantProcesses  || [],
        purchasedPrecursors: fA.purchasedPrecursors || [],
        processData:        cleanProcessData,
        precursorData,
        ghgTotals,
        totalDirectEmissions:   totalDirect,
        totalIndirectEmissions: totalIndirect,
      },

      // Tab 2 — Summary Products (same 44-col compute used in Excel export)
      summaryProducts: {
        processRows: cleanProcessData,
        precRows:    precursorData,
      },

      // Tab 3 — Summary Communication
      summaryCommunication: {
        processRows: cleanProcessData,
        precRows:    precursorData,
        ghgBalanceByMethod: fC?.ghgBalanceByMethod || {},
        dataQuality:        fC?.dataQuality || {},
        totalDirect,
        totalIndirect,
      },
    };
  };

  const handlePublish = async () => {
    setConfirmPublishOpen(false);
    if (!auditId || !currentUser?.email) return;
    //  Defence in depth — even though the button is hidden/disabled for
    // non-AppAdmin, an extra runtime check prevents accidental publish via
    // re-opened dialog state or browser tampering.
    if (!isAppAdmin) {
      setError('Only the tenant administrator can publish this audit.');
      return;
    }
    setActionInProgress(true);
    setError('');
    try {
      const snapshot = buildSummarySnapshot();
      await updateDoc(doc(firestore, 'auditReports', auditId), {
        ...snapshot,
        status: 'published',
        publishedAt: serverTimestamp(),
        publishedBy: currentUser.email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setStatus('published');
    } catch (err) {
      console.error('Publish failed:', err);
      setError('Failed to publish. Please try again.');
    } finally {
      setActionInProgress(false);
    }
  };

  if (loadingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !status) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      {!readOnly && (
    <AuditSidebar
        displayAuditId={auditId}
        formsData={formsData}
    />
)}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: '100%',
            pt: 3, pb: 4,
            px: { xs: 2, md: 4 },
            bgcolor: '#f5f5f5',
            minHeight: '100vh',
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

            {/* Header strip: status chip + action buttons */}
        <Paper
          elevation={2}
          sx={{
            p: 2, mb: 2, borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
  {readOnly ? 'Report Preview' : 'Report Summary'}
</Typography>
            {status && (
              <Chip
                label={status.toUpperCase()}
                color={STATUS_COLORS[status] || 'default'}
                size="small"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Box>

          {!readOnly && (
<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* PDF download button (always available, all states)
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
              sx={{
                borderColor: '#376366',
                color: '#376366',
                '&:hover': {
                  borderColor: '#2c5052',
                  backgroundColor: 'rgba(55, 99, 102, 0.04)',
                },
              }}
            >
              Download PDF
            </Button> */}
            {/* PDF download button — only available once submitted or published */}

            {/* Preview button — shown while still in draft, before PDF is available */}
{status === 'draft' && (
  <Button
    variant="outlined"
    startIcon={<PreviewIcon />}
    onClick={() => navigate(`/report-preview/${auditId}`)}
    sx={{
      borderColor: '#376366',
      color: '#376366',
      '&:hover': {
        borderColor: '#2c5052',
        backgroundColor: 'rgba(55, 99, 102, 0.04)',
      },
    }}
  >
    Preview
  </Button>
)}

{(status === 'submitted' || status === 'published') && (
  <Button
    variant="outlined"
    startIcon={<DownloadIcon />}
    onClick={handleDownloadPdf}
    sx={{
      borderColor: '#376366',
      color: '#376366',
      '&:hover': {
        borderColor: '#2c5052',
        backgroundColor: 'rgba(55, 99, 102, 0.04)',
      },
    }}
  >
    Download PDF
  </Button>
)}

            {status === 'draft' && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => setConfirmSubmitOpen(true)}
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Submitting…' : 'Submit'}
              </Button>
            )}

            {/*  NEW — Publish button gated on isAppAdmin.                    */}
            {/* AppUser sees the button but it's disabled, with a tooltip      */}
            {/* explaining why. (MUI requires a <span> wrapper around          */}
            {/* disabled buttons so the tooltip still fires on hover.)         */}
            {status === 'submitted' && (
              <Tooltip
                title={!isAppAdmin ? 'Only the tenant admin can publish this audit' : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setConfirmPublishOpen(true)}
                    disabled={!isAppAdmin || actionInProgress}
                  >
                    {actionInProgress ? 'Publishing…' : 'Publish'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {status === 'published' && (
              <Alert severity="success" sx={{ py: 0 }}>
                This audit is published and locked.
              </Alert>
            )}
          </Box>
)}
        </Paper>

        {/* Banner for submitted state */}
        {status === 'submitted' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This audit has been submitted. Review the summaries below. You can still go back
            and edit Form A–F if needed; the summary will recompute.{' '}
            {isAppAdmin
              ? <>Click <b>Publish</b> when you're ready to finalize.</>
              : <>The tenant admin will publish once review is complete.</>}
          </Alert>
        )}

        {/* Banner for published state */}
        {status === 'published' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This audit is finalized. The summary below reflects the values frozen at publish time.
            No further changes can be made.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* The 3 summary tabs */}
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="CBAM summary tabs"
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label={<Typography fontWeight="bold">Summary Processes</Typography>} {...a11yProps(0)} />
              <Tab label={<Typography fontWeight="bold">Summary Products</Typography>} {...a11yProps(1)} />
              <Tab label={<Typography fontWeight="bold">Communication Summary</Typography>} {...a11yProps(2)} />
            </Tabs>
          </Box>
        </Paper>

        <CustomTabPanel value={activeTab} index={0}>
          <Summary1Installation />
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index={1}>
  <SummaryProducts readOnly={readOnly} />
</CustomTabPanel>

<CustomTabPanel value={activeTab} index={2}>
  <SummaryCommunication readOnly={readOnly} />
</CustomTabPanel>
      </Box>
    </Box>
  </Box>

      {/* Confirm submit dialog */}
      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
        <DialogTitle>Submit audit for review?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once submitted, this audit moves to the submitted state. You can still go back and
            edit Form A–F afterwards — the summary will recompute live. Are you sure you want to
            submit now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="warning">
            Yes, submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm publish dialog */}
      <Dialog open={confirmPublishOpen} onClose={() => setConfirmPublishOpen(false)}>
        <DialogTitle>Publish audit?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Publishing freezes the audit. The summary values shown below will be saved
            permanently to Firestore. Form A–F will be locked from further edits and the
            summary cannot be changed. This action cannot be undone. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublishOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="success">
            Yes, publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SummaryTabs;